import { Component, OnInit, ViewChild } from '@angular/core';
import { ObservationDefinition } from 'app/mdm/lib/observation-definition/observation-definition';
import { ObservationDefinitionService } from 'app/mdm/lib/observation-definition/services/observation-definition.service';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CodeName, DateRange, markAsDirty } from '@heda/core-util';
import { Location } from '@angular/common';
import { ObservationDefinitionIntervalsTableComponent } from 'app/mdm/terminology/observation-definition/containers/components/observation-definition-intervals-table.component';
import { ObservationDefinitionIdentifiersTableComponent } from 'app/mdm/terminology/observation-definition/containers/components/observation-definition-identifiers-table.component';
import { ObservationDefinitionTargetsTableComponent } from 'app/mdm/terminology/observation-definition/containers/components/observation-definition-targets-table.component';
import { AuthContext } from '@heda/core-hospital-ui';
import { saveAs } from 'file-saver';
import { ObservationDefinitionDataSettingsComponent } from 'app/mdm/terminology/observation-definition/containers/components/observation-definition-data-settings.component';
import { ObservationDefinitionReferencesTableComponent } from 'app/mdm/terminology/observation-definition/containers/components/observation-definition-references-table.component';
import { Observable, of } from 'rxjs';
import { ObservationGroupService } from 'app/mdm/terminology/observation-group/services/observation-group.service';

@Component({
  templateUrl: './observation-definition-edit.component.html',
})
export class ObservationDefinitionEditComponent implements OnInit {
  private editPrivileges = ['mdm.terminology.edit'];
  public hasEditPrivileges = false;
  public definition: ObservationDefinition;
  public allGroups: CodeName[] = [];

  @ViewChild('form') public form: NgForm;
  @ViewChild('dataSettings') public dataSettings: ObservationDefinitionDataSettingsComponent;
  @ViewChild('identifiersTableComponent') public identifiersTable: ObservationDefinitionIdentifiersTableComponent;
  @ViewChild('referencesTableComponent') public referencesTable: ObservationDefinitionReferencesTableComponent;
  @ViewChild('intervalTableComponent') public intervalTable: ObservationDefinitionIntervalsTableComponent;
  @ViewChild('targetsTableComponent') public targetsTable: ObservationDefinitionTargetsTableComponent;

  constructor(
    private observationService: ObservationDefinitionService,
    private observationGroupService: ObservationGroupService,
    private route: ActivatedRoute,
    private authContext: AuthContext,
    private location: Location
  ) {}

  public ngOnInit(): void {
    this.route.params.subscribe((params) => {
      const id = params['definitionId'] ? Number(params['definitionId']) : null;
      this.init(id).subscribe((def) => {
        this.definition = def;
        this.definition.properties = this.definition.properties || {};
        this.definition.groups = this.definition.groups || [];
      });
    });
    this.observationGroupService.search({ limit: -1 }).subscribe((resp) => (this.allGroups = resp.data));
    this.hasEditPrivileges = this.authContext.hasAnyPrivilege(this.editPrivileges);
  }

  private init(definitionId: number): Observable<ObservationDefinition> {
    if (definitionId) {
      return this.observationService.load(definitionId, true);
    }
    const def = new ObservationDefinition();
    def.type = { code: 'manual' };
    def.period = new DateRange();
    def.identifiers = [];
    def.intervals = [];
    def.targets = [];
    def.references = [];
    def.groups = [];
    return of(def);
  }

  public save(): void {
    if (
      !this.form.valid ||
      !this.identifiersTable.validate() ||
      !this.referencesTable.validate() ||
      !this.intervalTable.validate() ||
      !this.targetsTable.validate() ||
      !this.dataSettings.validate()
    ) {
      markAsDirty(this.form);
      return;
    }
    const definition: ObservationDefinition = Object.assign({}, this.definition);
    definition.code = definition.code.trim();
    this.observationService.save(definition, definition.id).subscribe(() => this.location.back());
  }

  public exportObservationDefinition(): void {
    this.observationService.export(this.definition.code).subscribe((out) => {
      saveAs(new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' }), `${this.definition.code}.json`);
    });
  }
}
